/**
 * 流式事件处理工具
 * 处理 Dify API 的 Server-Sent Events (SSE) 响应
 */

/**
 * 读取并解析流式响应
 * @param {ReadableStream} body - fetch 响应的 body 流
 * @param {Object} handlers - 事件处理器
 * @param {Function} handlers.onMessage - 收到消息片段时回调 (text)
 * @param {Function} handlers.onEnd - 流结束时回调 (conversationId)
 * @param {Function} handlers.onError - 出错时回调 (error)
 * @param {Function} handlers.onAgentMessage - agent 消息回调
 * @param {AbortSignal} [signal] - 可选的取消信号
 */
export async function readStream(body, handlers, signal) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      if (signal && signal.aborted) break;

      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            handleEvent(data, handlers);
          } catch (e) {
            // 忽略解析错误
          }
        }
      }
    }

    // 处理剩余的 buffer
    if (buffer.startsWith('data: ')) {
      try {
        const data = JSON.parse(buffer.slice(6));
        handleEvent(data, handlers);
      } catch (e) {
        // 忽略
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function handleEvent(data, handlers) {
  const event = data.event;

  switch (event) {
    case 'message':
    case 'agent_message':
      if (data.answer) {
        handlers.onAgentMessage?.(data.answer);
        handlers.onMessage?.(data.answer);
      }
      break;
    case 'message_end':
      handlers.onEnd?.(data.conversation_id || '');
      break;
    case 'error':
      handlers.onError?.(data.message || '未知错误');
      break;
    case 'tts_message':
      // 语音消息，暂不处理
      break;
    case 'ping':
      // 心跳维持
      break;
  }

  if (data.task_id) {
    handlers.onTaskId?.(data.task_id);
  }
}

/**
 * 发送聊天消息并处理流式响应
 * @param {string} baseURL - API 基础地址
 * @param {string} apiKey - API 密钥
 * @param {Object} params - 请求参数
 * @param {string} params.query - 用户问题
 * @param {string} params.user - 用户ID
 * @param {string} [params.conversation_id] - 会话ID
 * @param {Object} [params.inputs] - 额外输入
 * @param {Object} handlers - 事件处理器
 * @param {AbortSignal} [signal] - 取消信号
 */
export async function sendStreamMessage(baseURL, apiKey, params, handlers, signal) {
  const requestData = {
    query: params.query,
    user: params.user,
    inputs: params.inputs || {},
    response_mode: 'streaming'
  };

  if (params.conversation_id) {
    requestData.conversation_id = params.conversation_id;
  }

  const response = await fetch(`${baseURL}chat-messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': apiKey ? `Bearer ${apiKey}` : '',
      'Accept': 'text/event-stream'
    },
    body: JSON.stringify(requestData),
    signal
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`HTTP错误: ${response.status} ${errorText}`);
  }

  await readStream(response.body, handlers, signal);
}

/**
 * 停止响应
 */
export async function stopStreamResponse(baseURL, apiKey, taskId, user) {
  const response = await fetch(`${baseURL}chat-messages/${taskId}/stop`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': apiKey ? `Bearer ${apiKey}` : ''
    },
    body: JSON.stringify({ user })
  });
  return response;
}
